import { useState, useMemo, useRef, useEffect, type ReactNode } from "react"
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  type SortingState,
} from "@tanstack/react-table"
import {
  PackageCheck,
  Search,
  Plus,
  CheckCircle2,
  ArrowUpRight,
  ArrowDownLeft,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  List,
  LayoutList,
  DollarSign,
  FileUp,
  CalendarClock,
  Send,
  Download,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Wallet,
  Building2,
  Receipt,
  FileText,
  CreditCard,
  Landmark,
  X,
} from "lucide-react"
import {
  ComposedChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  ReferenceLine,
  CartesianGrid,
} from "recharts"
import { Button } from "@/components/ui/Button"
import { Badge } from "@/components/ui/badge"
import { Avatar } from "@/components/ui/Avatar"
import { Tabs, TabsList, TabsTab, TabsPanel } from "@/components/ui/tabs"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Collapsible, CollapsibleTrigger, CollapsiblePanel } from "@/components/ui/collapsible"
import { Select, SelectTrigger, SelectValue, SelectPopup, SelectItem } from "@/components/ui/Select"
import { cn } from "@/utils/Functions"
import { notifyFeature } from "@/utils/demoNotifications"
import { useIsDemo } from "@/hooks/common/useIsDemo"
import { DeliverySheet } from "./DeliverySheet"
import type { DeliveryStatus, DeliveryItem, DeliveryItemDetailFields, PaymentAutomation } from "@/types/delivery-manager.types"
import { isPayment, isArtifact } from "@/types/delivery-manager.types"
import { statusConfig, automationConfig, frequencyLabels, categoryIcons, formatCents, shortDate } from "@/constants/delivery-manager.constants"
import { useDeliveryManagerData } from "@/hooks/delivery-manager/useDeliveryManagerData"
import type { ConnectedAccount } from "@/types/delivery-manager.types"
import { Input } from "@/components/ui"

// ═══════════════════════════════════════════════════════════════════════
//  Derived helpers
// ═══════════════════════════════════════════════════════════════════════

const TODAY_ISO = new Date().toISOString().slice(0, 10)

/** Whether a date is in the past relative to "today" */
function isPast(isoDate: string): boolean {
  return isoDate < TODAY_ISO
}

function getTimeline(item: DeliveryItem): "past" | "upcoming" {
  return item.status === "completed" || isPast(item.dueDate) ? "past" : "upcoming"
}

function sumPaymentCents(items: DeliveryItem[]): number {
  return items.reduce((sum, i) => (isPayment(i) ? sum + i.amountCents : sum), 0)
}

function formatLaneAmount(items: DeliveryItem[]): string {
  const cents = sumPaymentCents(items)
  return cents > 0 ? formatCents(cents) : "—"
}

// ═══════════════════════════════════════════════════════════════════════
//  Lane Engine
// ═══════════════════════════════════════════════════════════════════════

interface Lane {
  key: string
  label: string
  icon: ReactNode
  iconBg: string
  accentClass: string
  items: DeliveryItem[]
}

interface SplitGroup {
  key: string
  label: string
  icon: ReactNode
  iconBg: string
  accentClass: string
  predicate: (item: DeliveryItem) => boolean
}

interface Dimension {
  key: string
  label: string
  groups: SplitGroup[]
  allowCombined: boolean
}

type DimensionMode = "split" | "combined" | string

function computeLanes(
  items: DeliveryItem[],
  dimensions: Dimension[],
  modes: Record<string, DimensionMode>,
): Lane[] {
  // 1. Filter: narrow items for any dimension set to a specific value
  let filtered = items
  for (const dim of dimensions) {
    const mode = modes[dim.key]
    if (mode && mode !== "split" && mode !== "combined") {
      const group = dim.groups.find((g) => g.key === mode)
      if (group) filtered = filtered.filter(group.predicate)
    }
  }

  // 2. Split: compose all dimensions in "split" mode
  let lanes: Lane[] = [{
    key: "root", label: "", icon: null, iconBg: "", accentClass: "", items: filtered,
  }]

  for (const dim of dimensions) {
    if (modes[dim.key] !== "split") continue
    lanes = lanes.flatMap((lane) =>
      dim.groups.map((group) => ({
        key: lane.key === "root" ? group.key : `${lane.key}__${group.key}`,
        label: lane.label ? `${lane.label} · ${group.label}` : group.label,
        icon: group.icon,
        iconBg: group.iconBg,
        accentClass: group.accentClass,
        items: lane.items.filter(group.predicate),
      }))
    )
  }

  if (lanes.length === 1 && lanes[0].key === "root") {
    lanes[0].label = "All"
    lanes[0].icon = <PackageCheck className="size-3 text-muted-foreground" />
    lanes[0].iconBg = "bg-muted"
    lanes[0].accentClass = "text-muted-foreground"
  }

  return lanes
}

// ═══════════════════════════════════════════════════════════════════════
//  Dimension Definitions
// ═══════════════════════════════════════════════════════════════════════

const timelineDim: Dimension = {
  key: "timeline",
  label: "Timeline",
  allowCombined: false,
  groups: [
    { key: "past", label: "Completed", icon: <CheckCircle2 className="size-3 text-emerald-400" />, iconBg: "bg-emerald-400/10", accentClass: "text-emerald-400", predicate: (i) => getTimeline(i) === "past" },
    { key: "upcoming", label: "Upcoming", icon: <CalendarClock className="size-3 text-violet-400" />, iconBg: "bg-violet-400/10", accentClass: "text-violet-400", predicate: (i) => getTimeline(i) === "upcoming" },
  ],
}

const directionDim: Dimension = {
  key: "direction",
  label: "Direction",
  allowCombined: true,
  groups: [
    { key: "outbound", label: "Outgoing", icon: <Send className="size-3 text-red-400" />, iconBg: "bg-red-400/10", accentClass: "text-red-400", predicate: (i) => i.direction === "send" },
    { key: "inbound", label: "Incoming", icon: <Download className="size-3 text-emerald-400" />, iconBg: "bg-emerald-400/10", accentClass: "text-emerald-400", predicate: (i) => i.direction === "receive" },
  ],
}

const typeDim: Dimension = {
  key: "type",
  label: "Type",
  allowCombined: true,
  groups: [
    { key: "payments", label: "Payments", icon: <DollarSign className="size-3 text-blue-400" />, iconBg: "bg-blue-400/10", accentClass: "text-blue-400", predicate: (i) => i.kind === "payment" },
    { key: "artifacts", label: "Artifacts", icon: <FileUp className="size-3 text-amber-400" />, iconBg: "bg-amber-400/10", accentClass: "text-amber-400", predicate: (i) => i.kind === "artifact" },
  ],
}

const allDimensions: Dimension[] = [timelineDim, directionDim, typeDim]


// ═══════════════════════════════════════════════════════════════════════
//  Chart: derive from payment data
// ═══════════════════════════════════════════════════════════════════════

/**
 * A single point on the cash-flow chart.
 * Every payment is a point the line passes through.
 * Points that are *not* transactions act as anchors (month starts).
 */
interface ChartPoint {
  /** Fractional x position (month index + day fraction) */
  monthIdx: number
  /** Cumulative cash flow in cents */
  actual: number | null
  projected: number | null
  /** If this point is a transaction, its item ID */
  itemId?: string
  /** Human label for tooltip */
  label: string
  /** Transaction metadata (only on payment points) */
  txn?: {
    title: string
    amountCents: number
    direction: "send" | "receive"
    dueDate: string
  }
}

/** Month tick mark for the X axis */
interface MonthTick {
  idx: number
  label: string
}

interface ChartData {
  points: ChartPoint[]
  monthTicks: MonthTick[]
  todayX: number
}

function buildCashFlowChart(items: DeliveryItem[]): ChartData {
  const payments = items.filter(isPayment)

  // Build 9-month window centered roughly around today
  const todayMonth = TODAY_ISO.slice(0, 7)
  const startDate = new Date(TODAY_ISO)
  startDate.setMonth(startDate.getMonth() - 5)

  // Map month key -> month index (0-8)
  const monthToIdx: Record<string, number> = {}
  const monthTicks: MonthTick[] = []
  for (let i = 0; i < 9; i++) {
    const d = new Date(startDate)
    d.setMonth(d.getMonth() + i)
    const key = d.toISOString().slice(0, 7)
    monthToIdx[key] = i
    monthTicks.push({ idx: i, label: d.toLocaleString("en-US", { month: "short" }) })
  }

  // Helper: ISO date -> fractional monthIdx
  function dateToX(iso: string): number | null {
    const monthKey = iso.slice(0, 7)
    const idx = monthToIdx[monthKey]
    if (idx === undefined) return null
    const day = parseInt(iso.slice(8, 10), 10)
    const daysInMonth = new Date(parseInt(monthKey.slice(0, 4)), parseInt(monthKey.slice(5, 7)), 0).getDate()
    return idx + (day / daysInMonth)
  }

  // Sort all payments by date, then build cumulative series
  const sorted = [...payments].sort((a, b) => a.dueDate.localeCompare(b.dueDate))

  // First: collect all points (month-start anchors + transactions) with their x positions
  interface RawPoint {
    x: number
    delta: number  // net change at this point (0 for anchors)
    itemId?: string
    label: string
    txn?: ChartPoint["txn"]
  }
  const rawPoints: RawPoint[] = []

  // Add month-start anchor at x=0 (start of chart) if no transactions before it
  rawPoints.push({ x: 0, delta: 0, label: monthTicks[0].label })

  for (const p of sorted) {
    const x = dateToX(p.dueDate)
    if (x === null) continue
    const sign = p.direction === "receive" ? 1 : -1
    rawPoints.push({
      x,
      delta: p.amountCents * sign,
      itemId: p.id,
      label: `${p.title} (${shortDate(p.dueDate)})`,
      txn: { title: p.title, amountCents: p.amountCents, direction: p.direction, dueDate: p.dueDate },
    })
  }

  // Add end-of-chart anchor
  rawPoints.push({ x: 8, delta: 0, label: monthTicks[8].label })

  // Sort by x
  rawPoints.sort((a, b) => a.x - b.x)

  // Compute today's x position
  const todayX = dateToX(TODAY_ISO) ?? monthToIdx[todayMonth] ?? 4

  // Build final points with cumulative balances and actual/projected split
  let cumulative = 0
  const points: ChartPoint[] = rawPoints.map((rp) => {
    cumulative += rp.delta
    const isPast = rp.x <= todayX
    return {
      monthIdx: rp.x,
      actual: isPast ? cumulative : null,
      projected: isPast && rp.x === todayX ? cumulative : (!isPast ? cumulative : null),
      itemId: rp.itemId,
      label: rp.label,
      txn: rp.txn,
    }
  })

  // Fix the bridge point: the last "actual" point should also be the first "projected" point
  // Find the last actual point (highest x <= todayX)
  for (let i = points.length - 1; i >= 0; i--) {
    if (points[i].actual !== null) {
      points[i].projected = points[i].actual
      break
    }
  }

  return { points, monthTicks, todayX }
}

// ═══════════════════════════════════════════════════════════════════════
//  Collapsible Section
// ═══════════════════════════════════════════════════════════════════════

interface CollapsibleSectionProps {
  title: string
  icon: ReactNode
  defaultOpen?: boolean
  children: ReactNode
  trailing?: ReactNode
}

function CollapsibleSection({ title, icon, defaultOpen = true, children, trailing }: CollapsibleSectionProps) {
  return (
    <Collapsible defaultOpen={defaultOpen} className="border-2 border-border rounded-xl overflow-hidden">
      <CollapsibleTrigger className="group w-full px-3 py-2.5 flex items-center gap-2 bg-accent">
        <div className="size-5 rounded flex items-center justify-center bg-muted shrink-0">{icon}</div>
        <span className="font-semibold text-muted-foreground">{title}</span>
        {trailing && <div className="ml-1">{trailing}</div>}
        <div className="ml-auto">
          <ChevronDown className="size-3.5 text-muted-foreground transition-transform duration-200 group-data-panel-open:rotate-180" />
        </div>
      </CollapsibleTrigger>
      <CollapsiblePanel>
        <div className="p-3">
          {children}
        </div>
      </CollapsiblePanel>
    </Collapsible>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Main Component
// ═══════════════════════════════════════════════════════════════════════

type ViewMode = "table" | "feed"

type TimelineFilter = "all" | "past" | "upcoming"
type DirectionFilter = "all" | "send" | "receive"
type TypeFilter = "all" | "payment" | "artifact"

export function DeliveryManagerPage() {
  const isDemo = useIsDemo()
  const {
    items: mockDeliveryItems,
    detailData: mockDetailData,
    connectedAccounts: mockConnectedAccounts,
    equaliqBalanceCents: EQUALIQ_BALANCE_CENTS,
    emptyDetail,
  } = useDeliveryManagerData()
  const items: DeliveryItem[] = mockDeliveryItems
  const [viewMode, setViewMode] = useState<ViewMode>("feed")
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<"all" | DeliveryStatus>("all")
  const [timelineFilter, setTimelineFilter] = useState<TimelineFilter>("all")
  const [directionFilter, setDirectionFilter] = useState<DirectionFilter>("all")
  const [typeFilter, setTypeFilter] = useState<TypeFilter>("all")
  const [highlightedItemId, setHighlightedItemId] = useState<string | null>(null)
  const [modes, setModes] = useState<Record<string, DimensionMode>>({
    timeline: "split",
    direction: "combined",
    type: "combined",
  })

  const setMode = (dimKey: string, mode: DimensionMode) => {
    setModes((prev) => ({ ...prev, [dimKey]: mode }))
  }

  // Table filtering — uses same DeliveryItem data as feed
  const filteredTableItems = useMemo(() => items.filter((item) => {
    if (statusFilter !== "all" && item.status !== statusFilter) return false
    if (timelineFilter !== "all") {
      const timeline = getTimeline(item)
      if (timelineFilter !== timeline) return false
    }
    if (directionFilter !== "all" && item.direction !== directionFilter) return false
    if (typeFilter !== "all" && item.kind !== typeFilter) return false
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      const counterparty = item.direction === "send" ? item.to : item.from
      if (
        !item.title.toLowerCase().includes(q) &&
        !item.dealName.toLowerCase().includes(q) &&
        !counterparty.name.toLowerCase().includes(q)
      ) return false
    }
    return true
  }), [statusFilter, timelineFilter, directionFilter, typeFilter, searchQuery])

  const tableCounts = useMemo(() => {
    const counts: Record<string, number> = { all: items.length }
    for (const item of items) counts[item.status] = (counts[item.status] ?? 0) + 1
    return counts
  }, [items])

  // Feed: search filter then lane engine
  const searchFiltered = useMemo(() => {
    if (!searchQuery) return items
    const q = searchQuery.toLowerCase()
    return items.filter((i) => i.title.toLowerCase().includes(q) || i.dealName.toLowerCase().includes(q))
  }, [searchQuery])

  const lanes = useMemo(
    () => computeLanes(searchFiltered, allDimensions, modes),
    [searchFiltered, modes],
  )

  // Chart: derived from all payment items
  const chartData = useMemo(() => buildCashFlowChart(items), [items])
  const sendTotal = useMemo(() => sumPaymentCents(items.filter((i: DeliveryItem) => i.direction === "send")), [items])
  const receiveTotal = useMemo(() => sumPaymentCents(items.filter((i: DeliveryItem) => i.direction === "receive")), [items])

  const handleDotClick = (itemId: string) => {
    setHighlightedItemId((prev) => (prev === itemId ? null : itemId))
  }

  // Sheet state
  const [sheetOpen, setSheetOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState<(DeliveryItem & DeliveryItemDetailFields) | null>(null)

  const handleItemClick = (item: DeliveryItem) => {
    const detail = mockDetailData[item.id] ?? emptyDetail
    setSelectedItem({ ...item, ...detail })
    setSheetOpen(true)
  }

  const handleRelatedItemClick = (itemId: string) => {
    const item = items.find((i) => i.id === itemId)
    if (item) handleItemClick(item)
  }

  return (
    <Tabs defaultValue="feed" value={viewMode} onValueChange={(val) => setViewMode(val as ViewMode)} className="h-full flex flex-col bg-background">
      {/* Header */}
      <header className="shrink-0 border-b border-border bg-card/50">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center size-9 rounded-lg bg-muted">
                <PackageCheck className="size-[18px] text-muted-foreground" />
              </div>
              <div>
                <h1 className="text-base font-semibold text-foreground tracking-tight">Delivery Manager</h1>
                <p className="text-xs text-muted-foreground">Track payments and deliverables across your deals</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TabsList variant="default" indicatorColor="secondary" className="h-auto">
                <TabsTab value="feed" className="gap-1.5 text-xs">
                  <LayoutList className="size-3.5" />
                  Feed
                </TabsTab>
                <TabsTab value="table" className="gap-1.5 text-xs">
                  <List className="size-3.5" />
                  Table
                </TabsTab>
              </TabsList>
              <Button size="sm" onClick={() => notifyFeature("Create Deliverable", true)} className="gap-1.5">
                <Plus className="size-3.5" />
                New
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Table toolbar */}
      {viewMode === "table" && (
        <div className="shrink-0 px-6 py-3 border-b border-border flex items-center gap-3 flex-wrap">
          <div className="relative min-w-[200px] max-w-sm">
            <Input
              startIcon={<Search className="size-2 text-muted-foreground pointer-events-none" />}
              type="search"
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              aria-label="Search items"
            />
          </div>

          {/* Dimension filters */}
          <div className="flex items-center gap-2 ml-auto">
            <Select value={timelineFilter} onValueChange={(val: string | null) => { if (val) setTimelineFilter(val as TimelineFilter) }} items={[{ value: "all", label: "All" }, { value: "past", label: "Past" }, { value: "upcoming", label: "Upcoming" }]}>
              <SelectTrigger size="sm" className="min-w-0 w-auto h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectPopup>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="past">Past</SelectItem>
                <SelectItem value="upcoming">Upcoming</SelectItem>
              </SelectPopup>
            </Select>

            <Select value={directionFilter} onValueChange={(val: string | null) => { if (val) setDirectionFilter(val as DirectionFilter) }} items={[{ value: "all", label: "All" }, { value: "send", label: "Out" }, { value: "receive", label: "In" }]}>
              <SelectTrigger size="sm" className="min-w-0 w-auto h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectPopup>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="send">Out</SelectItem>
                <SelectItem value="receive">In</SelectItem>
              </SelectPopup>
            </Select>

            <Select value={typeFilter} onValueChange={(val: string | null) => { if (val) setTypeFilter(val as TypeFilter) }} items={[{ value: "all", label: "All" }, { value: "payment", label: "Payments" }, { value: "artifact", label: "Artifacts" }]}>
              <SelectTrigger size="sm" className="min-w-0 w-auto h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectPopup>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="payment">Payments</SelectItem>
                <SelectItem value="artifact">Artifacts</SelectItem>
              </SelectPopup>
            </Select>
          </div>
        </div>
      )}

      {/* Table status tabs */}
      {viewMode === "table" && (
        <div className="shrink-0 px-6 border-b border-border flex items-center">
          <Tabs value={statusFilter} onValueChange={(val) => setStatusFilter(val as "all" | DeliveryStatus)}>
            <TabsList variant="underline" indicatorColor="primary" className="h-auto gap-0">
              {(["all", "overdue", "in_progress", "pending", "upcoming", "completed"] as const).map((s) => (
                <TabsTab key={s} value={s} className="text-xs px-3 py-2">
                  {s === "all" ? "All" : statusConfig[s as DeliveryStatus]?.label ?? s}
                  <span className="ml-1 text-muted-foreground">{tableCounts[s] ?? 0}</span>
                </TabsTab>
              ))}
            </TabsList>
          </Tabs>
          <span className="ml-auto text-xs text-muted-foreground">{filteredTableItems.length} items</span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-h-0">
        <TabsPanel value="table" className="h-full" keepMounted>
          <div className="h-full overflow-auto">
            <TableView items={filteredTableItems} onItemClick={handleItemClick} />
          </div>
        </TabsPanel>
        <TabsPanel value="feed" className="h-full" keepMounted>
          <div className="flex flex-col gap-4 h-full overflow-y-auto p-4">
            {/* Balances */}
            <CollapsibleSection
              title="Balances"
              icon={<Wallet className="size-3 text-muted-foreground" />}
            >
              <BalancesPanel />
            </CollapsibleSection>

            {/* Cash Projection */}
            <CollapsibleSection
              title="Cash Projection"
              icon={<DollarSign className="size-3 text-muted-foreground" />}
              trailing={
                <Badge variant={(receiveTotal - sendTotal) >= 0 ? "success" : "error"}>
                  {(receiveTotal - sendTotal) >= 0 ? "+" : "−"}{formatCents(Math.abs(receiveTotal - sendTotal))}
                </Badge>
              }
            >
              <CashFlowChart chartData={chartData} sendTotal={sendTotal} receiveTotal={receiveTotal} highlightedItemId={highlightedItemId} onDotClick={handleDotClick} />
            </CollapsibleSection>

            {/* Create New */}
            <CollapsibleSection
              title="Create New"
              icon={<Plus className="size-3 text-muted-foreground" />}
              defaultOpen={false}
            >
              <CreateNewPanel />
            </CollapsibleSection>

            {/* Feed Filter + Lanes */}
            <FeedFilterBar dimensions={allDimensions} modes={modes} onModeChange={setMode} searchQuery={searchQuery} onSearchChange={setSearchQuery} />
            <LaneCarousel lanes={lanes} highlightedItemId={highlightedItemId} onHighlightClear={() => setHighlightedItemId(null)} onItemClick={handleItemClick} />
          </div>
        </TabsPanel>
      </div>

      <DeliverySheet
        item={selectedItem}
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        onRelatedItemClick={handleRelatedItemClick}
      />
    </Tabs>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Balances Panel
// ═══════════════════════════════════════════════════════════════════════

const accountTypeIcons: Record<ConnectedAccount["type"], typeof Landmark> = {
  checking: Landmark,
  savings: Building2,
  card: CreditCard,
}

function BalancesPanel() {
  const { connectedAccounts: mockConnectedAccounts, equaliqBalanceCents: EQUALIQ_BALANCE_CENTS } = useDeliveryManagerData()
  const totalConnected = mockConnectedAccounts.reduce((s: number, a: { balanceCents: number }) => s + a.balanceCents, 0)

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Equal-IQ Balance */}
        <div className="rounded-xl border border-border bg-muted/30 p-4">
          <div className="flex items-center gap-2 mb-2">
            <div className="size-7 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <Wallet className="size-3.5 text-violet-400" />
            </div>
            <span className="text-xs font-medium text-muted-foreground">Equal-IQ Balance</span>
          </div>
          <p className="text-2xl font-bold text-foreground tracking-tight">{formatCents(EQUALIQ_BALANCE_CENTS)}</p>
          <div className="flex items-center gap-2 mt-2">
            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => notifyFeature("Add Funds", true)}>
              <Plus className="size-3" /> Add Funds
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => notifyFeature("Withdraw", true)}>
              <ArrowUpRight className="size-3" /> Withdraw
            </Button>
          </div>
        </div>

        {/* Connected Accounts */}
        <div className="rounded-xl border border-border bg-muted/30 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="size-7 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Building2 className="size-3.5 text-blue-400" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Connected Accounts</span>
            </div>
            <button onClick={() => notifyFeature("Connect Account", true)} className="text-[10px] text-blue-400 hover:text-blue-300 font-medium transition-colors">
              + Connect
            </button>
          </div>
          <p className="text-lg font-bold text-foreground tracking-tight mb-2">{formatCents(totalConnected)}</p>
          <div className="space-y-1.5">
            {mockConnectedAccounts.map((acct) => {
              const Icon = accountTypeIcons[acct.type]
              return (
                <div key={acct.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="size-3 text-muted-foreground shrink-0" />
                    <span className="text-foreground/80 truncate">{acct.institution} ••{acct.lastFour}</span>
                  </div>
                  <span className="text-muted-foreground font-medium shrink-0 ml-2">{formatCents(acct.balanceCents)}</span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Create New Panel
// ═══════════════════════════════════════════════════════════════════════

const createActions = [
  {
    label: "Payment",
    description: "Send money to a party",
    icon: DollarSign,
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-400",
  },
  {
    label: "Invoice (Request)",
    description: "Request a payment from a party",
    icon: Receipt,
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-400",
  },
  {
    label: "Artifact",
    description: "Upload or request a deliverable",
    icon: FileText,
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-400",
  },
]

function CreateNewPanel() {
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {createActions.map((action) => (
          <button
            key={action.label}
            onClick={() => notifyFeature(`Create ${action.label}`, true)}
            className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 p-3 hover:bg-muted/60 transition-colors text-left group"
          >
            <div className={cn("size-9 rounded-lg flex items-center justify-center shrink-0", action.iconBg)}>
              <action.icon className={cn("size-4", action.iconColor)} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground group-hover:text-foreground/90">{action.label}</p>
              <p className="text-[11px] text-muted-foreground leading-tight">{action.description}</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Feed Filter Bar
// ═══════════════════════════════════════════════════════════════════════

interface FeedFilterBarProps {
  dimensions: Dimension[]
  modes: Record<string, DimensionMode>
  onModeChange: (dimKey: string, mode: DimensionMode) => void
  searchQuery: string
  onSearchChange: (q: string) => void
}

function FeedFilterBar({ dimensions, modes, onModeChange, searchQuery, onSearchChange }: FeedFilterBarProps) {
  return (
    <div className="shrink-0 px-6 py-2 border-b border-border flex items-center gap-4 overflow-x-auto">
      <div className="relative shrink-0">
        <Input
          startIcon={<Search className="size-2 text-muted-foreground pointer-events-none" />}
          type="search"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          aria-label="Search delivery manager"
        />
        {searchQuery && (
          <button
            aria-label="Clear search"
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        )}
      </div>
      <div className="ml-auto flex items-center gap-4">
        {dimensions.map((dim, idx) => {
          const currentMode = modes[dim.key] ?? "split"
          const options: { value: string; label: string }[] = [
            ...dim.groups.map((g) => ({ value: g.key, label: g.label })),
            { value: "split", label: "Split" },
            ...(dim.allowCombined ? [{ value: "combined", label: "Both" }] : []),
          ]
          return (
            <div key={dim.key} className="flex items-center gap-2 shrink-0">
              {idx > 0 && <div className="w-px h-4 bg-border shrink-0" />}
              <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider shrink-0">{dim.label}</span>
              <Select value={currentMode} onValueChange={(val: string | null) => { if (val) onModeChange(dim.key, val) }} items={options}>
                <SelectTrigger size="sm" className="min-w-0 w-auto h-7 text-[11px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectPopup>
                  {options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectPopup>
              </Select>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Table View (TanStack React Table)
// ═══════════════════════════════════════════════════════════════════════

const statusOrder: Record<DeliveryStatus, number> = { overdue: 0, in_progress: 1, pending: 2, upcoming: 3, completed: 4 }

function getCounterparty(item: DeliveryItem) {
  return item.direction === "send" ? item.to : item.from
}

const columnHelper = createColumnHelper<DeliveryItem>()

function SortHeader({ label, column }: { label: string; column: { getIsSorted: () => false | "asc" | "desc"; toggleSorting: (desc?: boolean) => void } }) {
  const sorted = column.getIsSorted()
  return (
    <button
      className="flex items-center gap-1.5 hover:text-foreground transition-colors"
      onClick={() => column.toggleSorting(sorted === "asc")}
    >
      {label}
      {sorted === "asc" ? (
        <ArrowUp className="size-3.5" />
      ) : sorted === "desc" ? (
        <ArrowDown className="size-3.5" />
      ) : (
        <ArrowUpDown className="size-3.5 opacity-50" />
      )}
    </button>
  )
}

const tableColumns = [
  columnHelper.accessor("title", {
    header: ({ column }) => <SortHeader label="Title" column={column} />,
    cell: ({ row }) => (
      <div>
        <span className="font-medium text-foreground">{row.original.title}</span>
        <p className="text-[11px] text-muted-foreground/70 line-clamp-1 mt-0.5">{row.original.description}</p>
      </div>
    ),
  }),
  columnHelper.accessor((row) => getCounterparty(row).name, {
    id: "counterparty",
    header: ({ column }) => <SortHeader label="Counterparty" column={column} />,
    cell: ({ row }) => {
      const cp = getCounterparty(row.original)
      return (
        <div className="flex items-center gap-2">
          <Avatar fallback={cp.initials} className="size-6 text-[10px]" />
          <span className="text-foreground/80 truncate">{cp.name}</span>
        </div>
      )
    },
  }),
  columnHelper.accessor("dealName", {
    header: ({ column }) => <SortHeader label="Deal" column={column} />,
    cell: ({ row }) => <span className="text-muted-foreground truncate block max-w-[160px]">{row.original.dealName}</span>,
  }),
  columnHelper.accessor("kind", {
    header: ({ column }) => <SortHeader label="Type" column={column} />,
    cell: ({ row }) => {
      const item = row.original
      if (isPayment(item)) {
        return <span className="inline-flex items-center gap-1 text-xs text-blue-400"><DollarSign className="size-3" />Payment</span>
      }
      if (isArtifact(item)) {
        const Icon = categoryIcons[item.category]
        return <span className="inline-flex items-center gap-1 text-xs text-amber-400"><Icon className="size-3" />{item.fileType}</span>
      }
      return null
    },
  }),
  columnHelper.accessor((row) => isPayment(row) ? row.amountCents : 0, {
    id: "amount",
    header: ({ column }) => <SortHeader label="Amount" column={column} />,
    cell: ({ row }) => {
      const item = row.original
      if (!isPayment(item)) return <span className="text-muted-foreground/40">—</span>
      const isSend = item.direction === "send"
      return (
        <span className={cn("text-sm font-semibold tabular-nums", isSend ? "text-red-400" : "text-emerald-400")}>
          {isSend ? "−" : "+"}{formatCents(item.amountCents)}
        </span>
      )
    },
  }),
  columnHelper.accessor("direction", {
    header: ({ column }) => <SortHeader label="Direction" column={column} />,
    cell: ({ row }) => {
      const isSend = row.original.direction === "send"
      return (
        <span className={cn("inline-flex items-center gap-1 text-xs font-medium", isSend ? "text-red-400" : "text-emerald-400")}>
          {isSend ? <ArrowUpRight className="size-3" /> : <ArrowDownLeft className="size-3" />}
          {isSend ? "Out" : "In"}
        </span>
      )
    },
  }),
  columnHelper.accessor("dueDate", {
    header: ({ column }) => <SortHeader label="Due Date" column={column} />,
    cell: ({ row }) => <span className="text-muted-foreground">{shortDate(row.original.dueDate)}</span>,
  }),
  columnHelper.accessor("status", {
    header: ({ column }) => <SortHeader label="Status" column={column} />,
    sortingFn: (rowA, rowB) => statusOrder[rowA.original.status] - statusOrder[rowB.original.status],
    cell: ({ row }) => {
      const StatusIcon = statusConfig[row.original.status].icon
      return (
        <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full", statusConfig[row.original.status].className)}>
          <StatusIcon className="size-3" />{statusConfig[row.original.status].label}
        </span>
      )
    },
  }),
  columnHelper.accessor((row) => isPayment(row) && row.automation ? row.automation.type : "zzz", {
    id: "automation",
    header: ({ column }) => <SortHeader label="Automation" column={column} />,
    cell: ({ row }) => {
      const item = row.original
      if (!isPayment(item) || !item.automation) return <span className="text-muted-foreground/40">—</span>
      const config = automationConfig[item.automation.type]
      const AutoIcon = config.icon
      return (
        <span className={cn("inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-lg border", config.className)}>
          <AutoIcon className="size-3" />{config.label}
        </span>
      )
    },
  }),
]

interface TableViewProps {
  items: DeliveryItem[]
  onItemClick: (item: DeliveryItem) => void
}

function TableView({ items, onItemClick }: TableViewProps) {
  const [sorting, setSorting] = useState<SortingState>([{ id: "dueDate", desc: false }])

  const table = useReactTable({
    data: items,
    columns: tableColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <Table>
      <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} className="text-xs text-muted-foreground">
                {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.length ? (
          table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.id}
              onClick={() => onItemClick(row.original)}
              className="cursor-pointer"
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={tableColumns.length} className="h-24 text-center text-muted-foreground">
              No items match your filters.
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Lane Carousel
// ═══════════════════════════════════════════════════════════════════════

function LaneCarousel({ lanes, highlightedItemId, onHighlightClear, onItemClick }: { lanes: Lane[]; highlightedItemId: string | null; onHighlightClear: () => void; onItemClick: (item: DeliveryItem) => void }) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({ left: dir === "left" ? -scrollRef.current.clientWidth * 0.6 : scrollRef.current.clientWidth * 0.6, behavior: "smooth" })
  }

  const useScroll = lanes.length > 2

  return (
    <div className="flex-1 min-h-0 relative">
      {useScroll && (
        <>
          <button onClick={() => scroll("left")} className="absolute left-1 top-1/2 -translate-y-1/2 z-20 size-8 rounded-full bg-background/90 border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
            <ChevronLeft className="size-4" />
          </button>
          <button onClick={() => scroll("right")} className="absolute right-1 top-1/2 -translate-y-1/2 z-20 size-8 rounded-full bg-background/90 border border-border shadow-md flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors">
            <ChevronRight className="size-4" />
          </button>
        </>
      )}
      <div ref={scrollRef} className={cn("flex h-full", useScroll && "overflow-x-auto snap-x snap-mandatory scroll-smooth")}>
        {lanes.map((lane, i) => (
          <div key={lane.key} className={cn("flex flex-col min-h-0 shrink-0 snap-start", useScroll ? "w-[45%] min-w-[320px]" : "flex-1", i < lanes.length - 1 && "border-r border-border")}>
            <div className="shrink-0 sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2 min-w-0">
                {lane.icon && <div className={cn("size-5 rounded flex items-center justify-center shrink-0", lane.iconBg)}>{lane.icon}</div>}
                <span className="text-[11px] font-semibold text-foreground uppercase tracking-wider truncate">{lane.label}</span>
                <Badge variant="secondary" size="sm">{lane.items.length}</Badge>
              </div>
              <span className={cn("text-[11px] font-semibold shrink-0 ml-2", lane.accentClass)}>{formatLaneAmount(lane.items)}</span>
            </div>
            <div className="flex-1 overflow-y-auto">
              {lane.items.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className="size-10 rounded-xl bg-muted flex items-center justify-center mb-2">{lane.icon ?? <PackageCheck className="size-4 text-muted-foreground/40" />}</div>
                  <p className="text-xs text-muted-foreground">Nothing here yet.</p>
                </div>
              ) : (
                <div className="py-1">{lane.items.map((item) => <FeedCard key={item.id} item={item} isHighlighted={highlightedItemId === item.id} isDimmed={!!highlightedItemId && highlightedItemId !== item.id} onClearHighlight={onHighlightClear} onItemClick={onItemClick} />)}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Cash Flow Chart
// ═══════════════════════════════════════════════════════════════════════

interface ChartTooltipProps {
  active?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload?: Array<{ value?: number; dataKey?: string; color?: string; payload?: any }>
}

function ChartTooltipContent({ active, payload }: ChartTooltipProps) {
  if (!active || !payload || payload.length === 0) return null

  const point = payload[0]?.payload as ChartPoint | undefined
  if (!point) return null

  // Get the balance (whichever is non-null)
  const balance = point.actual ?? point.projected ?? 0

  return (
    <div className="rounded-xl border border-border/70 bg-card/70 px-3 py-2 shadow-lg backdrop-blur-sm max-w-[220px]">
      {/* Transaction info if this is a payment point */}
      {point.txn ? (
        <>
          <p className="text-[11px] font-medium text-foreground truncate">{point.txn.title}</p>
          <div className="flex items-center justify-between gap-3 mt-0.5">
            <span className="text-[10px] text-muted-foreground">{shortDate(point.txn.dueDate)}</span>
            <span className={cn("text-[11px] font-semibold", point.txn.direction === "receive" ? "text-emerald-400" : "text-red-400")}>
              {point.txn.direction === "receive" ? "+" : "−"}{formatCents(point.txn.amountCents)}
            </span>
          </div>
          <div className="border-t border-border/50 mt-1.5 pt-1">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-muted-foreground">Balance</span>
              <span className="text-[11px] font-semibold text-foreground">{formatCents(balance)}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] text-muted-foreground">{point.label}</span>
          <span className="text-[11px] font-semibold text-foreground">{formatCents(balance)}</span>
        </div>
      )}
    </div>
  )
}

interface CashFlowChartProps {
  chartData: ChartData
  sendTotal: number
  receiveTotal: number
  highlightedItemId: string | null
  onDotClick: (itemId: string) => void
}

function CashFlowChart({ chartData, sendTotal, receiveTotal, highlightedItemId, onDotClick }: CashFlowChartProps) {
  const { points, monthTicks, todayX } = chartData
  const txnPoints = points.filter((p) => p.itemId)
  const paymentCount = txnPoints.length

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-5 text-[11px]">
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-emerald-400" />
            <span className="text-muted-foreground">Actual</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-emerald-400/40 border border-dashed border-emerald-400" />
            <span className="text-muted-foreground">Projected</span>
          </div>
          <span className="text-muted-foreground/50">
            {paymentCount} payments
          </span>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          <span className="text-red-400 font-medium">Out: {formatCents(sendTotal)}</span>
          <span className="text-emerald-400 font-medium">In: {formatCents(receiveTotal)}</span>
        </div>
      </div>
      <div className="h-[150px] -mx-2">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={points} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="actualGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-brand-green)" stopOpacity={0.2} />
                <stop offset="100%" stopColor="var(--color-brand-green)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="projectedGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--color-brand-green)" stopOpacity={0.08} />
                <stop offset="100%" stopColor="var(--color-brand-green)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid vertical={false} strokeDasharray="3 3" strokeOpacity={0.08} />
            <XAxis
              dataKey="monthIdx"
              type="number"
              domain={[0, 8]}
              axisLine={false}
              tickLine={false}
              ticks={monthTicks.map((t) => t.idx)}
              tickFormatter={(idx: number) => monthTicks.find((t) => t.idx === idx)?.label ?? ""}
              tick={{ fontSize: 10, fill: "#71717a" }}
              allowDuplicatedCategory={false}
            />
            <YAxis hide domain={["dataMin - 100000", "dataMax + 200000"]} />
            <RechartsTooltip content={<ChartTooltipContent />} />
            <ReferenceLine x={todayX} stroke="rgb(113, 113, 122)" strokeDasharray="3 3" strokeOpacity={0.4} label={{ value: "Today", position: "insideTopRight", fontSize: 9, fill: "#71717a" }} />
            {/* Actual line: straight segments through each transaction */}
            <Area
              dataKey="actual"
              type="linear"
              stroke="var(--color-brand-green)"
              strokeWidth={2}
              fill="url(#actualGrad)"
              connectNulls={false}
              dot={(dotProps: { cx: number; cy: number; index: number }) => {
                const pt = points[dotProps.index]
                // Only render dots for actual (past) transaction points
                if (!pt?.itemId || pt.actual === null) return <circle key={dotProps.index} r={0} />
                const isHighlighted = highlightedItemId === pt.itemId
                const color = pt.txn?.direction === "receive" ? "var(--color-brand-green)" : "var(--color-brand-red)"
                return (
                  <circle
                    key={pt.itemId}
                    cx={dotProps.cx}
                    cy={dotProps.cy}
                    r={isHighlighted ? 6 : 4}
                    fill={color}
                    stroke={isHighlighted ? "white" : color}
                    strokeWidth={isHighlighted ? 2 : 1}
                    opacity={highlightedItemId && !isHighlighted ? 0.3 : 1}
                    cursor="pointer"
                    onClick={() => onDotClick(pt.itemId!)}
                  />
                )
              }}
              activeDot={false}
            />
            {/* Projected line: dashed, same straight segments */}
            <Area
              dataKey="projected"
              type="linear"
              stroke="var(--color-brand-green)"
              strokeWidth={2}
              strokeDasharray="6 4"
              strokeOpacity={0.5}
              fill="url(#projectedGrad)"
              connectNulls={false}
              dot={(dotProps: { cx: number; cy: number; index: number }) => {
                const pt = points[dotProps.index]
                // Only render dots for projected-only transaction points (not overlapping with actual)
                if (!pt?.itemId || pt.actual !== null) return <circle key={dotProps.index} r={0} />
                const isHighlighted = highlightedItemId === pt.itemId
                const color = pt.txn?.direction === "receive" ? "var(--color-brand-green)" : "var(--color-brand-red)"
                return (
                  <circle
                    key={pt.itemId}
                    cx={dotProps.cx}
                    cy={dotProps.cy}
                    r={isHighlighted ? 6 : 4}
                    fill={color}
                    stroke={isHighlighted ? "white" : color}
                    strokeWidth={isHighlighted ? 2 : 1}
                    opacity={highlightedItemId && !isHighlighted ? 0.3 : 1}
                    cursor="pointer"
                    onClick={() => onDotClick(pt.itemId!)}
                  />
                )
              }}
              activeDot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Automation Badge
// ═══════════════════════════════════════════════════════════════════════

function automationDescription(a: PaymentAutomation): string {
  switch (a.type) {
    case "on_delivery":
      return `Triggers when "${a.deliverableTitle}" is delivered`
    case "on_date":
      return `Scheduled for ${shortDate(a.date)}`
    case "recurring":
      return `${frequencyLabels[a.frequency]} recurring payment`
    case "streaming":
      return `${a.source}${a.unitLabel ? ` · ${a.unitLabel}` : ""}`
    case "requires_approval":
      return `Approval needed from ${a.approverName}`
  }
}

function AutomationBadge({ automation }: { automation: PaymentAutomation }) {
  const config = automationConfig[automation.type]
  const Icon = config.icon

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-lg border text-[10px] font-medium cursor-pointer transition-colors hover:opacity-80",
        config.className,
      )}
      onClick={(e) => {
        e.stopPropagation()
        notifyFeature("Automation Settings", true)
      }}
    >
      <Icon className="size-3 shrink-0" />
      <span className="font-semibold">{config.label}</span>
      <span className="opacity-70">{automationDescription(automation)}</span>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════════════
//  Feed Card
// ═══════════════════════════════════════════════════════════════════════

interface FeedCardProps {
  item: DeliveryItem
  isHighlighted?: boolean
  isDimmed?: boolean
  onClearHighlight?: () => void
  onItemClick?: (item: DeliveryItem) => void
}

function FeedCard({ item, isHighlighted, isDimmed, onClearHighlight, onItemClick }: FeedCardProps) {
  const cardRef = useRef<HTMLDivElement>(null)
  const payment = isPayment(item)
  const StatusIcon = statusConfig[item.status].icon
  const isSend = item.direction === "send"
  const counterparty = isSend ? item.to : item.from

  // Auto-scroll highlighted card into view
  useEffect(() => {
    if (isHighlighted) {
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }
  }, [isHighlighted])

  return (
    <div
      ref={cardRef}
      onClick={() => {
        if (isHighlighted && onClearHighlight) {
          onClearHighlight()
        }
        onItemClick?.(item)
      }}
      className={cn(
        "flex items-start gap-3 pl-4 pr-4 py-3 cursor-pointer transition-all duration-200 border-l-2",
        isSend ? "border-l-red-400/50" : "border-l-emerald-400/50",
        isHighlighted
          ? "bg-blue-500/10 ring-1 ring-blue-400/40 ring-inset"
          : "hover:bg-muted/30",
        isDimmed && "opacity-40",
      )}
    >
      <div className="relative shrink-0 mt-0.5">
        <Avatar fallback={counterparty.initials} className="size-8" />
        <div className={cn("absolute -bottom-0.5 -right-0.5 size-4 rounded-full flex items-center justify-center border-2 border-background", isSend ? "bg-red-400/20" : "bg-emerald-400/20")}>
          {isSend ? <ArrowUpRight className="size-2.5 text-red-400" /> : <ArrowDownLeft className="size-2.5 text-emerald-400" />}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-foreground truncate">{counterparty.name}</span>
            <span className={cn("text-[10px] font-medium shrink-0", isSend ? "text-red-400" : "text-emerald-400")}>
              {isSend ? "↑ Outgoing" : "↓ Incoming"}
            </span>
          </div>
          {payment && (
            <span className={cn("text-sm font-semibold shrink-0", isSend ? "text-red-400" : "text-emerald-400")}>
              {isSend ? "−" : "+"}{formatCents(item.amountCents)}
            </span>
          )}
        </div>

        <p className="text-sm text-foreground/80 mt-0.5">{item.title}</p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{item.description}</p>

        {/* Automation badge */}
        {payment && item.automation && <AutomationBadge automation={item.automation} />}

        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {isArtifact(item) && (
            <Badge variant="secondary" size="sm" className="gap-1 text-[10px]">
              {(() => { const Icon = categoryIcons[item.category]; return <Icon className="size-2.5" /> })()}
              {item.fileType}
            </Badge>
          )}
          <span className="text-[10px] text-muted-foreground/60">{item.dealName}</span>
          <span className="text-[10px] text-muted-foreground/40">{shortDate(item.dueDate)}</span>
          <span className={cn("inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded-full", statusConfig[item.status].className)}>
            <StatusIcon className="size-2.5" />
            {statusConfig[item.status].label}
          </span>
        </div>
      </div>
    </div>
  )
}

export default DeliveryManagerPage
